import { IconButton, Typography } from "@material-tailwind/react";
import React from "react";
import { PlayIcon } from "@heroicons/react/24/outline";
import {
  MusicalNoteIcon,
  EyeIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import SharePopover from "./SharePopover";
import FavouriteBtn from "./FavouriteBtn";
import { DetailPageUrl } from "../services/API";
import { getYear, returnSubCategoryName } from "../helper/helper";
import { musicData } from "../Constant/Data";
import PlaylistItem from "./PlaylistItem";

export default function PlaylistComponent({
  rating = "4.0",
  views = 1,
  type = "movie",
  description,
  image,
  title,
  author,
  artist,
  sub_category = [],
  released_date,
  publish_date,
  duration_time = "",
  onClickBuy,
  isPurchased = false,
  onWishClick,
  onPlayClick,
  onFeaturedClick,
  feature_video,
  is_favourite,
  id,
  dark = false
}) {
  sub_category = returnSubCategoryName(sub_category);

  const playlistSongs = musicData; // Fallback to musicData
  const isPlaylist = type === "Audio" || type === "playlist";
  const typeName = isPlaylist ? "Playlist" : "Album";
  const navigate = useNavigate();

  const onPlay = () => {
    onPlayClick();
  };

  const onFirstBtnPress = () => {
    isPurchased ? onPlay() : onClickBuy();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-14 py-8">
      <section
        id="Detail"
        className={`relative overflow-hidden rounded-2xl border ${dark
          ? "bg-zinc-900/40 border-zinc-800/80 backdrop-blur-md shadow-2xl shadow-black/40"
          : "bg-white border-gray-150/80 shadow-xl"
          } p-6 sm:p-8 lg:p-12 mb-8`}
      >
        <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-16 items-center lg:items-stretch">
          {/* Metadata & Actions */}
          <div className="flex flex-col justify-center flex-1 w-full text-center lg:text-left">
            <span className="text-secondary text-xs sm:text-sm font-bold uppercase tracking-widest mb-2 block">
              {typeName}
            </span>
            <h2 className={`text-3xl lg:text-5xl font-bold tracking-tight mb-4 ${dark ? "text-white" : "text-black"}`}>
              {title || "Playlist Title"}
            </h2>

            <div className="flex items-center justify-center lg:justify-start gap-4 mb-4 text-xs sm:text-sm font-medium">
              <span className="flex items-center gap-1.5 text-secondary">
                <MusicalNoteIcon className="h-4 w-4" />
                {sub_category || "General"}
              </span>
              <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
              <span className="flex items-center gap-1.5 text-zinc-400">
                <EyeIcon className="h-4 w-4" />
                {views} views
              </span>
              {(publish_date || released_date) && (
                <>
                  <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
                  <span className="text-zinc-400">
                    {getYear(publish_date || released_date)}
                  </span>
                </>
              )}
            </div>

            {description && (
              <p className={`mb-6 text-sm sm:text-base leading-relaxed ${dark ? "text-zinc-400" : "text-gray-600"} line-clamp-3`}>
                {description}
              </p>
            )}

            <Typography
              className={`text-xs sm:text-sm mb-8 font-medium ${dark ? "text-zinc-400" : "text-gray-500"}`}
            >
              Created by <span className="font-semibold text-secondary">{artist || author || "Unknown Creator"}</span> • {playlistSongs.length} Tracks {duration_time && `• ${duration_time}`}
            </Typography>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <button
                onClick={onFirstBtnPress}
                className="w-full sm:w-auto px-8 py-3 rounded-lg font-bold text-sm bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/20 transition-all duration-300 hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2"
              >
                <PlayIcon className="h-5 w-5 fill-white text-white" />
                {!isPurchased && "Buy and "} Play Now
              </button>

              {feature_video && (
                <button
                  onClick={onFeaturedClick}
                  className="w-full sm:w-auto px-6 py-3 rounded-lg font-bold text-sm border border-zinc-700 hover:border-secondary bg-white/5 hover:bg-white/10 text-white transition-all duration-300 hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2"
                >
                  Watch Promo
                </button>
              )}

              <div className="flex items-center gap-4 mt-2 sm:mt-0">
                <FavouriteBtn is_favourite={is_favourite} onClick={onWishClick} dark={dark} />
                <SharePopover link={`${DetailPageUrl}?id=${id}`} dark={dark} />
              </div>
            </div>
          </div>

          {/* Cover Image Section */}
          <div className="w-48 sm:w-64 lg:w-80 flex-shrink-0">
            <div className="relative group rounded-2xl overflow-hidden shadow-2xl shadow-black/50 aspect-square border border-zinc-700/30">
              <img
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                src={
                  image ||
                  "https://ntvb.tmsimg.com/assets/p7825626_v_h10_aa.jpg?w=1280&h=720"
                }
                alt={title}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Tracks List Table */}
      <div
        className={`rounded-2xl border ${dark ? "bg-zinc-900/20 border-zinc-800/80" : "bg-white border-gray-150"
          } overflow-hidden shadow-xl`}
      >
        <div className="px-6 py-5 border-b border-zinc-800/60 flex items-center justify-between">
          <Typography className={`text-lg font-bold ${dark ? "text-white" : "text-black"}`}>
            Track List
          </Typography>
          <span className="text-xs font-bold text-secondary bg-secondary/10 px-2.5 py-1 rounded-md">
            {playlistSongs.length} TRACKS
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead>
              <tr className={dark ? "bg-zinc-950/40" : "bg-gray-50"}>
                <th className={`px-5 py-4 border-b ${dark ? "border-zinc-800" : "border-gray-200"} text-left text-xs font-bold uppercase tracking-wider ${dark ? "text-zinc-400" : "text-gray-500"}`}>
                  Song
                </th>
                <th className={`px-5 py-4 border-b ${dark ? "border-zinc-800" : "border-gray-200"} text-center text-xs font-bold uppercase tracking-wider ${dark ? "text-zinc-400" : "text-gray-500"}`}>
                  Artist
                </th>
                <th className={`px-5 py-4 border-b ${dark ? "border-zinc-800" : "border-gray-200"} text-center text-xs font-bold uppercase tracking-wider ${dark ? "text-zinc-400" : "text-gray-500"}`}>
                  Released
                </th>
                <th className={`px-5 py-4 border-b ${dark ? "border-zinc-800" : "border-gray-200"} text-center text-xs font-bold uppercase tracking-wider ${dark ? "text-zinc-400" : "text-gray-500"}`}>
                  Duration
                </th>
              </tr>
            </thead>
            <tbody>
              {playlistSongs.map((item, i) => (
                <PlaylistItem key={i} {...item} isActive={i === 1} dark={dark} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

